import { useState } from 'react'

import { Modal, ModalTitle } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalFooterWithActionsType } from '@/components/modal/constants'

import { type RemoveMemberResult } from './types'

interface RemoveMemberConfirmModalProps {
  memberName: string
  matterName: string
  onClose: () => void
  onConfirm: () => Promise<RemoveMemberResult>
}

/**
 * Destructive confirmation shown before removing a member from a matter. Stacks above the
 * Manage Members modal and owns its own loading/error state; the success toast fires from
 * the container once removal resolves.
 */
export function RemoveMemberConfirmModal({ memberName, matterName, onClose, onConfirm }: RemoveMemberConfirmModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setError(null)
    setLoading(true)

    try {
      const removeError = await onConfirm()

      if (!removeError) {
        // onClose unmounts this modal; the container surfaces the success toast.
        onClose()
      } else {
        setError(removeError)
        setLoading(false)
      }
    } catch {
      // onConfirm resolves its own errors, but guard against an unexpected rejection
      // so the spinner never locks on and the user can retry or cancel.
      setError('Could not remove member. Try again.')
      setLoading(false)
    }
  }

  return (
    <Modal open onOpenChange={(nextOpen) => !nextOpen && !loading && onClose()}>
      <ModalContainer width="sm" height="max-sm" stacked preventDismiss={loading} className="max-w-[448px]">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-1.5">
            <ModalTitle className="font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">Remove member?</ModalTitle>
            <p className="font-sans text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
              {memberName} will lose access to {matterName}.
            </p>
          </div>
          <ModalFooterWithActions
            type={ModalFooterWithActionsType.DESTRUCTIVE}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
            primaryAction={{ label: 'Remove', onClick: handleConfirm, loading }}
          />
        </div>
      </ModalContainer>
    </Modal>
  )
}
